import { Text, TextColor, TextVariant } from '@vizit/atoms';
import { Heading, HeadingVariant } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { cardClasses, containerClasses } from '../product-page-performance/styles';
import type { ProductPagePerformanceUnavailableProps } from './types';

// The scorecard's main score box, which now renders through ScoreStickerCard (N/A for a null
// score), so the withheld card keeps its own box to show a dash instead.
const SCORE_BOX = 'flex flex-col items-center justify-center gap-4 shrink-0 self-stretch bg-secondary px-4 py-6';

/**
 * Shown instead of the product page performance scorecard when a formula-served PDP's score is
 * withheld: the same score box with a dash instead of a number, and one sentence saying why.
 * The reason code only arrives for internal users (the API strips it for customers).
 */
const ProductPagePerformanceUnavailable = React.forwardRef<HTMLDivElement, ProductPagePerformanceUnavailableProps>(
  ({ reason = null, className, ...other }, forwardedRef) => (
    <div
      ref={forwardedRef}
      className={cx(containerClasses(), className)}
      data-testid="product-page-performance-unavailable"
      {...other}
    >
      <Heading variant={HeadingVariant.H6}>Product page performance</Heading>
      <div className={cardClasses({ isProminent: false })}>
        <div className={SCORE_BOX}>
          <Text variant={TextVariant.MonoXs} color={TextColor.Quaternary} showEllipsis={false}>
            PRODUCT PAGE SCORE
          </Text>
          {/* The dash is decorative; a plain span can't carry an aria-label, so the name is sr-only text. */}
          <span className="font-mono text-display-sm font-bold text-quaternary">
            <span aria-hidden="true">–</span>
            <span className="sr-only">No score</span>
          </span>
        </div>
        <div className="flex flex-col justify-center gap-1 self-stretch px-4">
          <Text variant={TextVariant.SmSemibold} color={TextColor.Primary} showEllipsis={false}>
            Score unavailable
          </Text>
          <Text variant={TextVariant.SmRegular} color={TextColor.Tertiary} showEllipsis={false}>
            We couldn't calculate a score for this page yet. This isn't about your content, and there's nothing to
            change.
          </Text>
          {reason != null && (
            <Text variant={TextVariant.MonoXs} color={TextColor.Tertiary} showEllipsis={false} className="pt-2">
              Reason: {reason}
            </Text>
          )}
        </div>
      </div>
    </div>
  ),
);

ProductPagePerformanceUnavailable.displayName = 'ProductPagePerformanceUnavailable';

export { ProductPagePerformanceUnavailable };
